import React, { useRef, useEffect, Suspense, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useFormikContext, getIn } from 'formik';
import {
  FarmScheduleCoverageTypesByState,
  FarmScheduleCoverageType,
  lookupsJson,
  FarmRiskType
} from '@ourbranch/lookups';
import { getDefaultFarmCoverageC, getDefaultFarmCoverageD } from '@ourbranch/farm-utils';

import editIcon from 'core/assets/svg/edit.svg';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import { useToast } from 'core/components/toast';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { Tooltip } from 'core/components/tooltip';
import { LabelTooltip } from 'core/components/label-tooltip';
import { appConfig } from 'brand-config';
import {
  SwimmingPoolForm,
  InlandMarineForm,
  RelatedPrivateStructuresForm,
  WellPumpForm,
  DwellingUnderConstructionForm,
  InlandFloodCoverageForm,
  UnscheduledPersonalPropertyForm,
  EarthquakeCoverageForm,
  SprinklerLeakageForm,
  BusinessPropertyForm
} from '../sub-risks';
import { BarndomininumDwellingForm } from 'common/components/farm/barndominium-dwelling-form';
import { MobileHomeForm } from 'common/components/farm/mobile-home-form';
import { RenovationFieldsForm } from 'common/components/farm/renovation-fields-form';
import {
  CosmeticDamageExclusionSection,
  RoofExclusionSection,
  ExteriorWallSurfacingExclusionSection,
  OtherStructuresExclusionSection,
  StormDamageExclusionSection
} from '../exclusions';
import { Banner } from 'common/components/banner';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import { useStore } from 'core/store';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmOpenSections } from 'common/hooks/farm-open-sections-context';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { formatCoverageB } from '../../helpers';
import {
  getDwellingConstructionTypeOptions,
  getPropertyTypeOptions,
  getMaskedResidenceUsageOptions
} from 'common/helpers/farm-helpers';
import { validateRoofYear } from './FO6.utils';
import { getLossSettlementDescriptionText, isFunctionalReplacementCostApplied } from './functional-replacement-cost';
import {
  isBarndominium as isBarndominiumBuilding,
  isMobileHome as isMobileHomeBuilding,
  isModularHome as isModularHomeBuilding
} from './property-type-helpers';

import useStyles from '../risk.styles';

const numFamiliesInDwellingOptions = lookupsJson.farm.numFamiliesInDwellingOptions;
const basementTypeOptions = lookupsJson.basementType;
const exteriorWallTypeOptions = lookupsJson.exteriorWallType;
const garageTypeOptions = lookupsJson.garageType;
const homeQualityOptions = lookupsJson.homeQuality;
const lossSettlementOptions = lookupsJson.farm.lossSettlement;
const roofLossSettlementOptions = lookupsJson.farm.roofLossSettlement;
const propertyTypeOptions = lookupsJson.farm.propertyType;
const roofShapeOptions = lookupsJson.roofShape;
const roofTypeOptions = lookupsJson.farm.dwellingRoofType;
const waterBackupOptions = lookupsJson.farm.waterBackupLimit;
const windHailDeductibleOptions = lookupsJson.farm.windstormOrHail;
const heatingSources = lookupsJson.farm.primaryHeatingSourceOptions;

const FO3 = ({ item, index, fieldName, removeRisk, setRiskFieldnameToEdit, canRemoveRisk, locationAddress }) => {
  const classes = useStyles();
  const {
    offer: { offer, selectedOption }
  } = useStore();
  const constructionTypeOptions = getDwellingConstructionTypeOptions(FarmRiskType.Fo3, selectedOption);
  // Mask-aware: e.g. Home (Z) must not offer Tenant Occupied Rental (#31691)
  const residenceUsageOptions = getMaskedResidenceUsageOptions(
    FarmRiskType.Fo3,
    selectedOption,
    locationAddress?.state
  );
  const { inPolicyRoute } = useRouteHelper();
  const { setFieldValue, errors, initialValues, values } = useFormikContext();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const { isCurrentSectionOpen, toggleSection } = useFarmOpenSections(fieldName);

  const debounceTimerRef = useRef(null);
  const toast = useToast();
  const rejections = offer?.rejections || [];
  const address = locationAddress;
  const coverageA = item.coverages?.coverageA;
  const hasBeenRated = !!item.id;

  // Mobile/modular detection accepts both `propertyType` and the
  // legacy `typeOfHome` field via the shared helper, matching the
  // dec mapper's `isMobileOrModularRC`. (#30735 audit alignment.)
  const isBarndominium = isBarndominiumBuilding(item?.building);
  const isMobileHome = isMobileHomeBuilding(item?.building);
  const isModularHome = isModularHomeBuilding(item?.building);
  const needMoreData = rejections.some((rejection) => rejection.code === '50006') || ['FO4', 'FO6'].includes(item.type);

  const state = address?.state;
  const riskType = FarmRiskType.Fo3;
  const availableCoverageTypes = FarmScheduleCoverageTypesByState[state] || [];
  // Get Mine Subsidence coverage limit from appConfig (GitHub issue #28634)
  const mineSubsidenceCoverageLimit = appConfig.farm?.[state]?.coverageLimits?.mineSubsidenceCoverageLimit || 750000;

  const checkIfCoverageIsEnabled = (coverageName) =>
    !appConfig.farm?.[state]?.risks?.[riskType]?.hiddenCoverages?.includes(coverageName);

  // Coverage C business rules drive the default (GitHub issue #29084). For tenant
  // (residenceUsage=R) the rule is {min:5000, max:25000}, so the default must be
  // $5,000 — not 75% of Coverage A, which would be clamped to $25,000 later.
  const businessRulesC = appConfig.farm?.[state]?.risks?.[riskType]?.businessRules?.coverageC;
  // Coverage D rules drive the percent-of-Coverage-A defaults (GitHub issue #30548).
  // R = 10%, P/S = 20%, V = $0 — read from brand config, no hardcoded percentages.
  const businessRulesD = appConfig.farm?.[state]?.risks?.[riskType]?.businessRules?.coverageD;

  const riskCoverages = getIn(values, fieldName)?.coverages || {};

  const initialCoverageA = getIn(initialValues, `${fieldName}.coverages.coverageA`);
  const initialBuilding = getIn(initialValues, `${fieldName}.building`);
  const initialConnectedHome = getIn(initialValues, `${fieldName}.connectedHome`);
  const initialCanEditCoverageAmount = getIn(initialValues, `${fieldName}.coverages.canEditCoverageAmount`);
  const coverageHasBeenUpdatedRef = useRef(false);

  // Roof year validation state
  const [currentRoofYear, setCurrentRoofYear] = useState(item.building?.roofYear);
  const [currentYearBuilt, setCurrentYearBuilt] = useState(item.building?.yearBuilt);
  const [roofYearError, setRoofYearError] = useState('');

  const yearBuilt = currentYearBuilt ?? item.building?.yearBuilt;
  const isFunctionalRC = isFunctionalReplacementCostApplied(riskCoverages.lossSettlement, yearBuilt);

  // Update ref when coverage changes
  useEffect(() => {
    if (hasBeenRated) {
      coverageHasBeenUpdatedRef.current =
        initialCoverageA !== item.coverages?.coverageA &&
        initialCanEditCoverageAmount === item.coverages?.canEditCoverageAmount;
    }
  }, [initialCoverageA, item.coverages?.coverageA]);

  const toastRCEWarning = () => {
    if (coverageHasBeenUpdatedRef.current) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // NOTE: No Formik sync needed for FO-3 - item.building is source of truth
  // State updates happen only through onChange handlers

  // Validate roof year when roofYear or yearBuilt changes
  useEffect(() => {
    const validation = validateRoofYear(currentRoofYear, currentYearBuilt);
    setRoofYearError(validation.isValid ? '' : validation.message);
  }, [currentRoofYear, currentYearBuilt]);

  const isSelectedHeatingSourceUnitCountBased =
    heatingSources.find((option) => option.id === item?.building?.primaryHeatingSource)?.unitCountBased ?? false;

  const riskTitle = hasBeenRated
    ? `FO-3 Special: ${propertyTypeOptions.find((option) => option.id === item?.building?.propertyType)?.value}`
    : `Dwelling: ${propertyTypeOptions.find((option) => option.id === item?.building?.propertyType)?.value}`;

  // Keep the LOCKED Coverage D in sync with its brand-config percentage of Coverage A
  // (P/S = 20%, R = 10%) whenever Coverage A or residence usage changes from ANY source —
  // not only the Coverage A field's onChange. When a dwelling is added during a policy-mod
  // endorsement, Coverage A is populated by the server's RCE data pull (not typed), so the
  // onChange-only derivation never fired and the locked Coverage D was left stale; the field
  // is not editable, so the user could not correct it and was blocked. GitHub issue #31480.
  const lockedResidenceUsage = item?.building?.residenceUsage;
  useEffect(() => {
    if (disableRiskForm || !['P', 'S', 'R'].includes(lockedResidenceUsage)) {
      return;
    }
    if (coverageA == null || Number.isNaN(Number(coverageA))) {
      return;
    }
    const expectedCoverageD = getDefaultFarmCoverageD({
      residenceUsage: lockedResidenceUsage,
      coverageA,
      businessRulesD
    });
    // Guard with !== to keep this idempotent (no setFieldValue loop).
    if (item?.coverages?.coverageD !== expectedCoverageD) {
      setFieldValue(`${fieldName}.coverages.coverageD`, expectedCoverageD);
    }
  }, [
    coverageA,
    lockedResidenceUsage,
    businessRulesD,
    item?.coverages?.coverageD,
    disableRiskForm,
    fieldName,
    setFieldValue
  ]);

  // #31820: Coverage C's default is seeded against the initial Coverage A ($75k). When the
  // valuation raises Coverage A (e.g. to the RCE), a stale seed Coverage C can fall below the
  // rule minimum (75% of A for FO3) and trip "Coverage C must be at least …" on load — unlike
  // Coverage D, which already rescales via the effect above. Clamp Coverage C UP to the rule
  // default when it's below it; never reduce a higher agent-entered value.
  useEffect(() => {
    // Skip Vacant (and any non-P/S/R) — same gate as the Coverage D effect above. Vacant
    // Coverage C is $0, and without a 'V' rule getDefaultFarmCoverageC would fall back to a
    // % of Coverage A and wrongly clamp it up. (devlo review)
    if (disableRiskForm || !['P', 'S', 'R'].includes(lockedResidenceUsage)) {
      return;
    }
    if (coverageA == null || Number.isNaN(Number(coverageA))) {
      return;
    }
    const defaultCoverageC = getDefaultFarmCoverageC({
      riskType,
      residenceUsage: lockedResidenceUsage,
      lossSettlement: item?.coverages?.lossSettlement,
      coverageA,
      businessRulesC
    });
    const currentCoverageC = item?.coverages?.coverageC;
    if (currentCoverageC != null && defaultCoverageC != null && currentCoverageC < defaultCoverageC) {
      setFieldValue(`${fieldName}.coverages.coverageC`, defaultCoverageC);
    }
  }, [
    coverageA,
    lockedResidenceUsage,
    item?.coverages?.lossSettlement,
    item?.coverages?.coverageC,
    businessRulesC,
    riskType,
    disableRiskForm,
    fieldName,
    setFieldValue
  ]);

  // Set default values for ownership and home type for new risks only
  useEffect(() => {
    if (!isBarndominium && !isMobileHome) {
      // Only set defaults if values don't already exist (new risk)
      if (!item?.building?.ownershipStatus) {
        setFieldValue(`${fieldName}.building.ownershipStatus`, 'P');
      }
      if (!item?.building?.typeOfHome) {
        setFieldValue(`${fieldName}.building.typeOfHome`, '1');
      }
      if (!item?.building?.residenceUsage) {
        setFieldValue(`${fieldName}.building.residenceUsage`, 'P');
      }
    }
  }, [
    fieldName,
    isBarndominium,
    isMobileHome,
    item?.building?.ownershipStatus,
    item?.building?.typeOfHome,
    item?.building?.residenceUsage,
    setFieldValue
  ]);

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">{riskTitle}</Label>
            <div className={classes.divider} />
            <Label type="infoSubtitleSmall">{item.description}</Label>
            <IconButton onClick={() => setRiskFieldnameToEdit(fieldName)} className={classes.editButton}>
              <img className={classes.editIcon} src={editIcon} alt="Edit row" />
            </IconButton>
          </div>
        </div>
      }
      isRemovable={canRemoveRisk}
      onDelete={() => removeRisk(index)}
      disableRemoveButton={disableRiskForm}
      open={isCurrentSectionOpen}
      onChange={toggleSection}
      id={fieldName}
    >
      <Suspense
        fallback={
          <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
            <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
              <CircularProgress className={classes.loader} />
            </Grid>
          </Grid>
        }
      >
        {!hasBeenRated && !isBarndominium && !isMobileHome && (
          <Banner
            variant="notification"
            title={
              inPolicyRoute
                ? "We'll pull data for this dwelling or structure—go ahead and preview changes."
                : "We'll pull data for this dwelling or structure—go ahead and update the offer."
            }
            subtitle="Or enter the details manually if you prefer."
          />
        )}
        {((hasBeenRated && needMoreData) || (!hasBeenRated && (isBarndominium || isMobileHome))) && (
          <Banner
            variant="warning"
            title="Data for this dwelling or structure isn't available—please enter the details manually."
            subtitle={
              inPolicyRoute
                ? "This information won't auto-fill, even if you preview changes."
                : "This information won't auto-fill, even if you update the offer."
            }
          />
        )}
        <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
          {!isBarndominium && !isMobileHome && (
            <>
              <Grid item xs={12}>
                <Label type="infoTitleSmall">Residents</Label>
              </Grid>
              {/* Issue #30827: Structure Category is a property characteristic and must be editable
                  wherever Residence Type/Usage is editable. */}
              <Field
                xs={6}
                type="select"
                name={`${fieldName}.building.propertyType`}
                id={`${fieldName}.building.propertyType`}
                mode="light"
                label="Structure Category"
                options={getPropertyTypeOptions(FarmRiskType.Fo3)}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.propertyType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                name={`${fieldName}.building.residenceUsage`}
                id={`${fieldName}.building.residenceUsage`}
                mode="light"
                label="Residence Type/Usage"
                options={residenceUsageOptions}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.residenceUsage) {
                    toastRCEWarning();
                  }
                  // Recompute Coverage C default for the new residence usage (GitHub issue #29084).
                  // Without this, switching P/S → R leaves a stale large Coverage C that gets
                  // clamped to $25,000 tenant max by post-data-rules, looking like "defaults to max".
                  // Guard against undefined coverageA (not yet set) — would produce NaN via the
                  // minPctOfCoverageA branch for P/S.
                  const defaultCoverageC = getDefaultFarmCoverageC({
                    riskType,
                    residenceUsage: val,
                    lossSettlement: item?.coverages?.lossSettlement,
                    coverageA: item?.coverages?.coverageA ?? 0,
                    businessRulesC
                  });
                  setFieldValue(`${fieldName}.coverages.coverageC`, defaultCoverageC);
                }}
              />
              <Grid item xs={12}>
                <Label type="infoTitleSmall">Property</Label>
              </Grid>
              <Field
                xs={4}
                name={`${fieldName}.building.yearBuilt`}
                id={`${fieldName}.building.yearBuilt`}
                type="numeric"
                label="Year Built"
                mode="light"
                format={{
                  allowNegative: false,
                  isAllowed: ({ value }) => value.length <= 4
                }}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.yearBuilt) {
                    toastRCEWarning();
                  }
                  setCurrentYearBuilt(val);
                  setFieldValue(`${fieldName}.building.yearBuilt`, val);
                  if (
                    isFunctionalReplacementCostApplied(riskCoverages.lossSettlement, val) &&
                    riskCoverages?.replacementCostExp
                  ) {
                    setFieldValue(`${fieldName}.coverages.replacementCostExp`, false);
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.sqFt`}
                name={`${fieldName}.building.sqFt`}
                mode="light"
                label="Above-ground sqft"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.sqFt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.basementSqFt`}
                name={`${fieldName}.building.basementSqFt`}
                mode="light"
                label="Basement sqft"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.basementSqFt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                options={numFamiliesInDwellingOptions}
                id={`${fieldName}.building.numFamiliesInDwelling`}
                name={`${fieldName}.building.numFamiliesInDwelling`}
                mode="light"
                label="Families living"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFamiliesInDwelling) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                options={homeQualityOptions}
                id={`${fieldName}.building.homeQuality`}
                name={`${fieldName}.building.homeQuality`}
                mode="light"
                label="Home Quality"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.homeQuality) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numStories`}
                name={`${fieldName}.building.numStories`}
                mode="light"
                label="Stories"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numStories) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numHalfBathrooms`}
                name={`${fieldName}.building.numHalfBathrooms`}
                mode="light"
                label="Half Bathrooms"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numHalfBathrooms) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numFullBathrooms`}
                name={`${fieldName}.building.numFullBathrooms`}
                mode="light"
                label="Full Bathrooms"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFullBathrooms) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="select"
                options={constructionTypeOptions}
                id={`${fieldName}.building.constructionType`}
                name={`${fieldName}.building.constructionType`}
                mode="light"
                label="Construction Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.constructionType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.numFireplaces`}
                name={`${fieldName}.building.numFireplaces`}
                mode="light"
                label="Fireplaces"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFireplaces) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.numWoodBurningStoves`}
                name={`${fieldName}.building.numWoodBurningStoves`}
                mode="light"
                label="Wood Stoves"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numWoodBurningStoves) {
                    toastRCEWarning();
                  }
                }}
              />
              <Grid item xs={4}>
                <LabelTooltip
                  label="Solid Fuel-Burning Appliances"
                  tooltip={{
                    label: 'More Info',
                    onHoverText:
                      'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
                  }}
                  mode="light"
                >
                  <Field
                    type="numeric"
                    id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                    name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                    mode="light"
                    format={{
                      allowNegative: false
                    }}
                    disabled={disableRiskForm}
                    onChange={(val) => {
                      if (val !== initialBuilding?.numSolidFuelBurningAppliances) {
                        toastRCEWarning();
                      }
                    }}
                  />
                </LabelTooltip>
              </Grid>
              <Field
                xs={4}
                type="select"
                options={basementTypeOptions}
                id={`${fieldName}.building.basementType`}
                name={`${fieldName}.building.basementType`}
                mode="light"
                label="Basement/Foundation Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.basementType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="select"
                options={exteriorWallTypeOptions}
                id={`${fieldName}.building.exteriorWallType`}
                name={`${fieldName}.building.exteriorWallType`}
                mode="light"
                label="Exterior Wall Finish"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.exteriorWallType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.garageCarCapacity`}
                name={`${fieldName}.building.garageCarCapacity`}
                mode="light"
                label="Garage's Car Spaces"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.garageCarCapacity) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="select"
                options={garageTypeOptions}
                id={`${fieldName}.building.garageType`}
                name={`${fieldName}.building.garageType`}
                mode="light"
                label="Garage Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.garageType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.roofYear`}
                name={`${fieldName}.building.roofYear`}
                mode="light"
                label="Roof Year"
                format={{
                  allowNegative: false,
                  isAllowed: ({ value }) => value.length <= 4
                }}
                disabled={disableRiskForm}
                error={!!getIn(errors, `${fieldName}.building.roofYear`) || !!roofYearError}
                helperText={getIn(errors, `${fieldName}.building.roofYear`) || roofYearError || undefined}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofYear) {
                    toastRCEWarning();
                  }
                  setCurrentRoofYear(val);
                  setFieldValue(`${fieldName}.building.roofYear`, val);
                }}
              />
              <Field
                xs={4}
                id={`${fieldName}.building.roofType`}
                name={`${fieldName}.building.roofType`}
                type="select"
                options={roofTypeOptions}
                mode="light"
                label="Roof type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.roofShape`}
                name={`${fieldName}.building.roofShape`}
                type="select"
                options={roofShapeOptions}
                mode="light"
                label="Roof Shape"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofShape) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.primaryHeatingSource`}
                name={`${fieldName}.building.primaryHeatingSource`}
                type="select"
                options={heatingSources}
                mode="light"
                label="Primary Heating"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.primaryHeatingSource) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.secondaryHeatingSource`}
                name={`${fieldName}.building.secondaryHeatingSource`}
                type="select"
                options={heatingSources}
                mode="light"
                label="Secondary Heating"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.secondaryHeatingSource) {
                    toastRCEWarning();
                  }
                }}
              />
              <RenovationFieldsForm
                basePath={fieldName}
                disabled={disableRiskForm}
                onFieldChange={() => toastRCEWarning()}
              />
              <Field
                xs={6}
                id={`${fieldName}.connectedHome.theftPreventionDeviceType`}
                name={`${fieldName}.connectedHome.theftPreventionDeviceType`}
                type="checkbox"
                mode="light"
                label="Theft protective device"
                disabled={disableRiskForm}
                checkedValue="centralStation"
                uncheckedValue="none"
                onChange={(_e, checked) => {
                  const initialIsChecked = initialConnectedHome?.theftPreventionDeviceType === 'centralStation';
                  if (checked !== initialIsChecked) {
                    toastRCEWarning?.();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
                name={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
                type="checkbox"
                mode="light"
                label="Fire protective device"
                disabled={disableRiskForm}
                checkedValue="centralStation"
                uncheckedValue="none"
                onChange={(_e, checked) => {
                  const initialIsChecked = initialConnectedHome?.fireProtectiveDeviceType === 'centralStation';
                  if (checked !== initialIsChecked) {
                    toastRCEWarning?.();
                  }
                }}
              />
              <Field
                xs={6}
                name={`${fieldName}.connectedHome.internetMonitoring`}
                id={`${fieldName}.connectedHome.internetMonitoring`}
                type="checkbox"
                label="Internet connected monitoring device(s)?"
                mode="light"
                disabled={disableRiskForm}
                className={classes.checkboxContainer}
                onChange={(val) => {
                  if (val !== initialConnectedHome?.internetMonitoring) {
                    toastRCEWarning?.();
                  }
                }}
              />
            </>
          )}

          {/* Barndominium specific fields */}
          {isBarndominium && (
            <BarndomininumDwellingForm
              basePath={fieldName}
              isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
              disableRiskForm={disableRiskForm}
              initialBuilding={initialBuilding}
              initialConnectedHome={initialConnectedHome}
              toastRCEWarning={toastRCEWarning}
              state={state}
              setFieldValue={setFieldValue}
            />
          )}

          {/* Mobile Home specific fields */}
          {isMobileHome && (
            <MobileHomeForm
              basePath={fieldName}
              isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
              disableRiskForm={disableRiskForm}
              initialBuilding={initialBuilding}
              initialConnectedHome={initialConnectedHome}
              toastRCEWarning={toastRCEWarning}
              state={state}
              riskType={riskType}
              setFieldValue={setFieldValue}
            />
          )}

          {(hasBeenRated || coverageHasBeenUpdatedRef.current) && (
            <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
              <Grid container xs={8} className={classes.rceLabel}>
                <Label>Our estimated cost to replace this home</Label>
                <Tooltip
                  label="More info"
                  text="RCE (Replacement Cost Estimate) and ACV (Actual Cash Value) are calculated using 360Value. RCE is the cost to replace the dwelling with new materials. ACV accounts for depreciation based on age and condition. The selected loss settlement option determines which value applies to the claim."
                />
              </Grid>
              <Grid container item xs={4} spacing={2} justifyContent="flex-end">
                <Grid item className={classes.rceValues}>
                  <Label>
                    RCE
                    {item.coverages?.lossSettlement === 'RC' || item.coverages?.lossSettlement === 'FUNC'
                      ? ' (Active)'
                      : ''}
                  </Label>
                  <Label type="infoValueBold">
                    {item.building?.replacementCostEstimate?.total
                      ? `$${item.building.replacementCostEstimate.total.toLocaleString('en-US')}`
                      : 'N/A'}
                  </Label>
                </Grid>
                {item.building?.actualCashValueCalculation?.total && (
                  <Grid item className={classes.rceValues}>
                    <Label>
                      ACV
                      {item.coverages?.lossSettlement === 'ACV' ? ' (Active)' : ''}
                    </Label>
                    <Label type="infoValueBold">
                      ${item.building.actualCashValueCalculation.total.toLocaleString('en-US')}
                    </Label>
                  </Grid>
                )}
              </Grid>
            </NotificationCard>
          )}
        </Grid>

        {(hasBeenRated || coverageHasBeenUpdatedRef.current) && (
          <Grid container spacing={2} className={classes.coverageCard}>
            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle">Base coverages</Label>
            </Grid>
            <Grid item xs={12}>
              <LabelTooltip
                label="Dwelling Limit (A)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'The amount we will pay to rebuild the home is based on the replacement cost estimate value—calculated using current construction, materials and labor costs. If you want added peace of mind, adjust the amount.'
                }}
              >
                <Field
                  id={`${fieldName}.coverages.coverageA`}
                  name={`${fieldName}.coverages.coverageA`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disableRiskForm}
                  onChange={(value) => {
                    // Clear previous timer
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }

                    // Set new timer — batch updates to prevent cascading renders in React 17 (#29818)
                    debounceTimerRef.current = setTimeout(() => {
                      ReactDOM.unstable_batchedUpdates(() => {
                        // Coverage C default respects brand-config rules (GitHub issue #29084).
                        // Tenant (R) gets the rule's min ($5,000), not 75% of Coverage A.
                        const defaultCoverageC = getDefaultFarmCoverageC({
                          riskType,
                          residenceUsage: item?.building?.residenceUsage,
                          lossSettlement: item?.coverages?.lossSettlement,
                          coverageA: value,
                          businessRulesC
                        });
                        setFieldValue(`${fieldName}.coverages.coverageC`, defaultCoverageC);
                        // Auto-set coverageD to brand-config percentage of CovA for non-Vacant
                        // (P/S = 20%, R = 10%) — GitHub issues #29013, #29958, #30548.
                        const isPrimarySeasonalOrRental = ['P', 'S', 'R'].includes(item?.building?.residenceUsage);
                        if (isPrimarySeasonalOrRental) {
                          setFieldValue(
                            `${fieldName}.coverages.coverageD`,
                            getDefaultFarmCoverageD({
                              residenceUsage: item?.building?.residenceUsage,
                              coverageA: value,
                              businessRulesD
                            })
                          );
                        }
                      });
                    }, 100);
                  }}
                  description={
                    !getIn(errors, `${fieldName}.coverages.coverageA`)
                      ? (() => {
                          // Issue #28846: Coverage A is now always editable with validation
                          const canAdjustMore = item.coverages?.canEditCoverageAmount;
                          if (canAdjustMore) {
                            return `Minimum: $${Math.round(initialCoverageA * 0.9).toLocaleString()} (90% RCE). No maximum with justification.`;
                          }
                          return `You may adjust this from $${Math.round(initialCoverageA * 0.9).toLocaleString()} (90% RCE) to $${initialCoverageA.toLocaleString()} (100% RCE).`;
                        })()
                      : undefined
                  }
                />
              </LabelTooltip>
            </Grid>

            <div className={classes.coverageToggleContainer}>
              <Grid container justifyContent="space-between">
                <Field
                  id={`${fieldName}.coverages.canEditCoverageAmount`}
                  name={`${fieldName}.coverages.canEditCoverageAmount`}
                  type="switch"
                  mode="dark"
                  label="Adjust dwelling limit more"
                  tooltipText="You may adjust the dwelling limit to an even higher amount with justification"
                  tooltipLabel="More Info"
                  xs={8}
                  disabled={disableRiskForm}
                  onChange={(_value, isToggledOn) => {
                    // When toggling off, reset coverageA to RCE total
                    if (!isToggledOn && item.building?.replacementCostEstimate?.total) {
                      const resetValue = item.building.replacementCostEstimate.total;
                      setFieldValue(`${fieldName}.coverages.coverageA`, resetValue);
                      // Recalculate Coverage D to match new Coverage A
                      // (P/S = 20%, R = 10% from brand config) — GitHub issues #29958, #30548.
                      if (['P', 'S', 'R'].includes(item?.building?.residenceUsage)) {
                        setFieldValue(
                          `${fieldName}.coverages.coverageD`,
                          getDefaultFarmCoverageD({
                            residenceUsage: item?.building?.residenceUsage,
                            coverageA: resetValue,
                            businessRulesD
                          })
                        );
                      }
                    }
                  }}
                />
                <Grid className={classes.linkAndIcon}>
                  <a
                    rel="noreferrer noopener"
                    target="_blank"
                    href="https://www.360-value.com/apps/iv/ui/#/login"
                    className={classes.link}
                  >
                    ISO 360 Login
                    <img src={externalLinkIcon} alt="link to ISO 360 login" />
                  </a>
                </Grid>

                {item.coverages?.canEditCoverageAmount && (
                  <Grid item xs={12} className={classes.adjustCoverageSubContainer}>
                    <Field
                      id={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                      name={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                      type="textarea"
                      mode="dark"
                      label="Description of reason for adjusting dwelling limit (required)"
                    />
                  </Grid>
                )}
              </Grid>
            </div>

            <Grid item xs={4}>
              <LabelTooltip
                label="Other Structures (B)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'Provides coverage for buildings and structures on the insured property that are separate from the main dwelling but within 250 feet, such as detached garages, sheds, and fences. This does not include buildings or structures designed for farming use. To add additional related private structure coverage, schedule them below.'
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageB`}
                  name={`${fieldName}.coverages.coverageB`}
                  type="value"
                  mode="dark"
                  value={formatCoverageB(item.coverages)}
                  disabled={disableRiskForm}
                  fast={false} // need this so react updates the options when coverageA changes
                  riskType={riskType}
                  state={state}
                  classes={classes}
                  coverages={riskCoverages}
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={4}>
              <LabelTooltip
                label="Personal Property (C)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'If something happens to personal property, like theft or damage due to fire, this covers those items.'
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageC`}
                  name={`${fieldName}.coverages.coverageC`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disableRiskForm}
                  riskType={riskType}
                  state={state}
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={4}>
              <LabelTooltip
                xs={4}
                label="Addit. Living Cost (D)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'If the home becomes uninhabitable due to some kind of covered loss, this coverage provides temporary housing up to the amount entered here.'
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageD`}
                  name={`${fieldName}.coverages.coverageD`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled
                  disableRiskForm={disableRiskForm}
                  fast={true}
                  riskType={riskType}
                  state={state}
                  coverages={riskCoverages}
                  riskFieldName={fieldName}
                />
              </LabelTooltip>
            </Grid>

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Deductibles
              </Label>
            </Grid>
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleAllOther`}
              name={`${fieldName}.coverages.deductibleAllOther`}
              type="select"
              mode="dark"
              label="All peril deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleWindHail`}
              name={`${fieldName}.coverages.deductibleWindHail`}
              type="select"
              options={windHailDeductibleOptions}
              mode="dark"
              label="Wind & Hail deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              coverages={riskCoverages}
              classes={classes}
              state={state}
              coverage={riskCoverages?.coverageA}
              description="Wind and hail deductible must match an All Peril when it's value is greater than 2% of coverage A"
            />

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Optional Coverages
              </Label>
            </Grid>

            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.lossSettlement`}
              name={`${fieldName}.coverages.lossSettlement`}
              type="select"
              options={lossSettlementOptions}
              mode="dark"
              label="Building Loss Settlement"
              disabled
              riskType={riskType}
              state={state}
              description={getLossSettlementDescriptionText(
                riskCoverages.lossSettlement,
                item.building,
                currentYearBuilt ?? item.building?.yearBuilt
              )}
            />

            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.roofLossSettlement`}
              name={`${fieldName}.coverages.roofLossSettlement`}
              type="select"
              options={roofLossSettlementOptions}
              mode="dark"
              label="Roof Loss Settlement"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              coverages={riskCoverages}
            />
            <Field
              xs={6}
              id={`${fieldName}.coverages.coverageWaterBackup`}
              name={`${fieldName}.coverages.coverageWaterBackup`}
              type="select"
              options={waterBackupOptions}
              mode="dark"
              label="Water Back Up & Sump Discharge/Overflow"
              disabled={disableRiskForm}
            />

            <BrandFormField
              name={`${fieldName}.coverages.coverageMS`}
              id={`${fieldName}.coverages.coverageMS`}
              type="select"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              coverages={riskCoverages}
              county={address?.county}
              targetCoverage={coverageA}
              targetCoverageLimit={mineSubsidenceCoverageLimit}
              helperTextLabel="Matches coverage A"
            />
            <Field
              name={`${fieldName}.coverages.hasActiveCoverageMS`}
              id={`${fieldName}.coverages.hasActiveCoverageMS`}
              type="checkbox"
              label="This dwelling has active mine subsidence or unrepaired mine damage"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
            />

            {/* checkboxes */}
            <BrandFormField
              name={`${fieldName}.coverages.repairRebuildRequirement`}
              id={`${fieldName}.coverages.repairRebuildRequirement`}
              type="checkbox"
              label="Repair or rebuilding requirement"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              riskType={riskType}
              riskFieldName={fieldName}
              state={state}
            />

            <Field
              name={`${fieldName}.coverages.serviceLine`}
              id={`${fieldName}.coverages.serviceLine`}
              type="checkbox"
              label="Service line coverage"
              mode="dark"
              // tooltipText="COMING SOON"
              // tooltipLabel="More Info"
              xs={6}
              disabled={disableRiskForm}
            />
            <BrandFormField
              name={`${fieldName}.coverages.personalPropertyReplCost`}
              id={`${fieldName}.coverages.personalPropertyReplCost`}
              type="checkbox"
              label="Personal property replacement cost"
              mode="dark"
              xs={6}
              riskType={riskType}
              state={state}
            />
            <Field
              name={`${fieldName}.coverages.replacementCostExp`}
              id={`${fieldName}.coverages.replacementCostExp`}
              type="checkbox"
              label="Expanded replacement cost"
              mode="dark"
              xs={6}
              disabled={disableRiskForm || isFunctionalRC}
            />
            {(isMobileHome || isModularHome) && (
              <BrandFormField
                name={`${fieldName}.coverages.replCostMobileHome`}
                id={`${fieldName}.coverages.replCostMobileHome`}
                type="checkbox"
                label="Replacement cost terms for mobile home"
                mode="dark"
                xs={6}
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
                coverages={riskCoverages}
              />
            )}

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Exclusions/Restrictions
              </Label>
            </Grid>

            {/* cosmetic damage exclusion */}
            <CosmeticDamageExclusionSection
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            <RoofExclusionSection
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            <ExteriorWallSurfacingExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

            <OtherStructuresExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

            {inPolicyRoute && (
              <StormDamageExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
            )}

            <SwimmingPoolForm fieldName={fieldName} disabled={disableRiskForm} />
            <EarthquakeCoverageForm
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />
            {/* unscheduled personal property */}
            <UnscheduledPersonalPropertyForm
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              state={state}
              riskType={riskType}
            />

            {/* inland marine */}
            <InlandMarineForm
              fieldName={`${fieldName}.coverages.scheduleCoverages`}
              disabled={disableRiskForm}
              state={state}
              riskType={FarmRiskType.Fo3}
              coverages={riskCoverages}
            />
            {/* related private structures */}
            {availableCoverageTypes.includes(FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES) && (
              <RelatedPrivateStructuresForm
                fieldName={`${fieldName}.coverages.scheduleCoverages`}
                disabled={disableRiskForm}
                address={locationAddress}
                riskType={riskType}
                coverages={riskCoverages}
              />
            )}

            {/* well pump */}
            <WellPumpForm fieldName={`${fieldName}`} disabled={disableRiskForm} />

            {/* dwelling under construction */}
            <DwellingUnderConstructionForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

            {/* inland flood coverage */}
            {checkIfCoverageIsEnabled('inlandFlood') && (
              <InlandFloodCoverageForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
            )}

            {/* business property */}
            {checkIfCoverageIsEnabled('businessProperty') && (
              <BusinessPropertyForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
            )}

            {/* sprinkler leakage */}
            {checkIfCoverageIsEnabled('sprinklerLeakage') && (
              <SprinklerLeakageForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
            )}
          </Grid>
        )}
      </Suspense>
    </CollapsibleCard>
  );
};

FO3.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  canRemoveRisk: PropTypes.bool.isRequired,
  removeRisk: PropTypes.func.isRequired,
  setRiskFieldnameToEdit: PropTypes.func.isRequired,
  locationAddress: PropTypes.object.isRequired
};

export default withDatePicker(FO3);
